import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { useStores } from "../../../use-stores";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Grid2,
  IconButton,
  Paper,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import CustomerTable from "../../components/customer/CustomerTable";
import PageTitle from "../../components/common/pages/PageTitle";
import { useNotification } from "../../../utils/NotificationsContext";
import AppDropDown from "../../components/common/filter/AppDropDown";
import DeleteConfirmation from "../../components/common/pop-up/DeleteConfirmation";
import { SortType } from "../../../models/SortType";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import { CustomerFilters } from "../../../models/Customer/CustomerFilters";
import CustomTypography from "../../components/common/form/CustomTypography";
import PermissionBlockLabel from "../../components/common/pop-up/PermissionBlockLabel ";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import FormTextField from "../../components/common/form/FormTextField";
import AppButton from "../../components/common/buttons/AppButton";
import AppSearchDropdownMulti from "../../components/common/filter/AppSearchDropdownMulti";
import CustomTextField from "../../components/common/textFields/TextField";

interface CustomerSelectedFilters {
  active: boolean;
}
const CustomerListing: React.FC = () => {
  const theme = useTheme();

  const { customerStore, userStore } = useStores();

  const { showError, showSuccess } = useNotification();

  const [deleteId, setDeleteId] = useState<number | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialParams: CustomerFilters = {
    page_no: searchParams.get("page")
      ? parseInt(searchParams.get("page") as string, 10)
      : 1,
    search_text: searchParams.get("search_text") || undefined,
    active: searchParams.get("active")
      ? searchParams.get("active") === "false"
        ? false
        : true
      : true,
    limit: searchParams.get("limit")
      ? parseInt(searchParams.get("limit") as string, 10)
      : 30,
    sort_by: searchParams.get("sort_by") || "createdDate",
    sort_type:
      searchParams.get("sort_type") === "ASC" ? SortType.ASC : SortType.DESC,
  };

  const initialFilters: CustomerSelectedFilters = {
    active: searchParams.get("active")
      ? searchParams.get("active") === "false"
        ? false
        : true
      : true,
  };

  const [filters, setFilters] =
    useState<CustomerSelectedFilters>(initialFilters);
  const [params, setParams] = useState(initialParams);

  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search_text") || "",
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    customerStore
      .getCustomers(params)
      .catch((err) =>
        showError(
          "Error",
          err?.response?.data?.message ||
            "Something went wrong! Please try again later.",
        ),
      )
      .finally(() => {
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    const updateQueryParams = () => {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (
          value !== undefined &&
          (!Array.isArray(value) || value.length > 0)
        ) {
          const paramValue = Array.isArray(value)
            ? value.join(",")
            : value.toString();
          searchParams.set(key, paramValue);
        }
      });

      const queryString = searchParams.toString();
      if (queryString) {
        navigate({ search: queryString }, { replace: true });
      }
    };

    updateQueryParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, navigate]);

  const handleSearchBar = async (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (e.target.value.length !== 0) {
      setSearchTerm(e.target.value);
    } else {
      setSearchTerm("");
      setParams((prev) => ({
        ...prev,
        search_text: "",
      }));
    }
  };

  const handleSearchSubmit = () => {
    setParams((prev) => ({
      ...prev,
      search_text: searchTerm,
      page_no: 1,
    }));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      if (searchTerm.trim().length > 0) {
        setParams((prev) => ({
          ...prev,
          search_text: searchTerm,
          page_no: 1,
        }));
      } else {
        setSearchTerm("");
        setParams((prev) => ({
          ...prev,
          search_text: "",
          page_no: 1,
        }));
      }
    }
  };

  const handleApply = () => {
    setParams((prev) => ({
      ...prev,
      active: filters.active,
    }));
  };

  const resetFilters = () => {
    setSearchTerm("");
    setFilters({
      active: true,
    });
    setParams({
      page_no: 1,
      limit: 30,
      sort_by: "createdDate",
      sort_type: SortType.DESC,
      active: true,
    });
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setParams((prev) => ({
      ...prev,
      page_no: newPage,
    }));
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setParams((prev) => ({
      ...prev,
      limit: newLimit,
      page_no: 1,
    }));
  };

  const handleDeleteConfirm = async (customerId: number) => {
    setIsLoading(true);
    if (customerId) {
      const res = await customerStore
        .deleteCustomer(customerId)
        .catch((err) => {
          showError(
            "Error",
            err?.response?.data?.message ||
              "Something went wrong! Please try again later.",
          );
        });
      setDeleteId(null);
      if (res?.success) {
        showSuccess(res?.result);
        customerStore
          .getCustomers(params)
          .catch((err) =>
            showError(
              "Error",
              err?.response?.data?.message ||
                "Something went wrong! Please try again later.",
            ),
          )
          .finally(() => setIsLoading(false));
      } else {
        setIsLoading(false);
        showError("Error Occurred", "Error deleting customer");
      }
    }
  };

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
      {!userStore.hasPermission([
        EmployeeRoles.VIEW_CUSTOMER,
        EmployeeRoles.EDIT_CUSTOMER,
        EmployeeRoles.CREATE_CUSTOMER,
      ]) && (
        <Paper>
          <Box
            sx={{
              marginTop: "0.6rem",
              paddingX: 4,
              paddingY: 2,
              boxShadow:
                "rgba(0, 0, 0, 0.25) 0px 0.0625em 0.0625em, rgba(0, 0, 0, 0.25) 0px 0.125em 0.5em, rgba(255, 255, 255, 0.1) 0px 0px 0px 1px inset",
              borderRadius: "4px",
              minHeight: { xs: "60vh", md: window.innerHeight - 110 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: 0.4,
                mb: 2,
              }}
            >
              <CustomTypography variant="heading3">Invoices</CustomTypography>
              <CustomTypography
                variant="body2"
                color={theme.palette.customColors?.greyTextColor}
              >
                View and manage invoices. Search by customer name, order ID, or
                invoice number.
              </CustomTypography>
            </Box>
            <PermissionBlockLabel />
          </Box>
        </Paper>
      )}
      {userStore.hasPermission([
        EmployeeRoles.VIEW_CUSTOMER,
        EmployeeRoles.EDIT_CUSTOMER,
        EmployeeRoles.CREATE_CUSTOMER,
      ]) && (
        <Paper>
          <Box
            sx={{
              marginTop: "0.6rem",
              paddingX: 4,
              paddingY: 2,
              boxShadow:
                "rgba(0, 0, 0, 0.25) 0px 0.0625em 0.0625em, rgba(0, 0, 0, 0.25) 0px 0.125em 0.5em, rgba(255, 255, 255, 0.1) 0px 0px 0px 1px inset",
              borderRadius: "4px",
              minHeight: { xs: "60vh", md: window.innerHeight - 110 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  gap: 0.5,
                }}
              >
                <CustomTypography variant="heading3">
                  Customers
                </CustomTypography>
                <CustomTypography
                  variant="body2"
                  color={theme.palette.customColors?.greyTextColor}
                >
                  View and manage customers.
                </CustomTypography>
              </Box>
            </Box>

            <Paper
              variant="outlined"
              sx={{
                mt: 2,
                borderRadius: 2,
                borderColor: "divider",
                overflow: "hidden",
              }}
            >
              <Box
                onClick={() => setIsSearchExpanded(!isSearchExpanded)}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  px: 2,
                  py: 0.5,
                  cursor: "pointer",
                  borderBottom: isSearchExpanded ? "1px solid" : "none",
                  borderColor: "divider",
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <CustomTypography variant="heading6">Search</CustomTypography>
                <IconButton size="small">
                  {isSearchExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
              <Collapse in={isSearchExpanded} timeout="auto" unmountOnExit>
                <Grid2 container spacing={2} columns={12} sx={{ p: 2 }}>
                  <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                    <CustomTextField
                      width={"100%"}
                      placeholder="Search customers by name"
                      value={searchTerm}
                      onChange={(e) => {
                        handleSearchBar(e);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleKeyDown(e);
                        }
                      }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <AppSearchDropdownMulti
                      title="Active"
                      isMultiSelect={false}
                      value={
                        filters.active
                          ? [{ id: "true", value: "Active" }]
                          : [{ id: "false", value: "Inactive" }]
                      }
                      list={[
                        { id: "true", value: "Active" },
                        { id: "false", value: "Inactive" },
                      ]}
                      local={true}
                      handleSelect={(selectedActive) => {
                        if (selectedActive.length > 0) {
                          setFilters((prev) => ({
                            ...prev,
                            active:
                              selectedActive[0].id === "true" ? true : false,
                          }));
                        }
                      }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}></Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}></Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <AppButton
                        variant="transparentPrimary"
                        label="Clear"
                        onClick={resetFilters}
                        customStyles={{ width: "100%" }}
                      />
                      <AppButton
                        variant="primary"
                        label="Apply"
                        onClick={handleApply}
                        customStyles={{ width: "100%" }}
                      />
                    </Box>
                  </Grid2>
                </Grid2>
              </Collapse>
            </Paper>
            <Box sx={{ maxHeight: window.innerHeight - 180 }}>
              <Box>
                {isLoading && (
                  <Box
                    sx={{
                      minHeight: "60vh",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <CircularProgress />
                  </Box>
                )}

                {!isLoading && (
                  <Box>
                    {customerStore.customers.length === 0 ? (
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        height="10rem"
                        textAlign="center"
                      >
                        <Typography
                          variant="h6"
                          fontWeight="500"
                          color="text.secondary"
                        >
                          No data found
                        </Typography>
                      </Box>
                    ) : (
                      <CustomerTable
                        maxHeight={window.innerHeight - 350}
                        customers={customerStore.customers}
                        limit={params.limit || customerStore.limit}
                        page_no={params.page_no}
                        total_count={customerStore.total_count}
                        total_pages={customerStore.total_pages}
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        onDelete={(id) => setDeleteId(id)}
                        onSortChange={(column, sortType) => {
                          setParams((prev) => ({
                            ...prev,
                            sort_by: column,
                            sort_type: sortType,
                          }));
                        }}
                        sortBy={params.sort_by}
                        sortType={params.sort_type}
                      />
                    )}

                    {deleteId !== null && (
                      <DeleteConfirmation
                        id={deleteId}
                        open={true}
                        onDeleteConfirm={() => handleDeleteConfirm(deleteId)}
                        onCancel={() => setDeleteId(null)}
                        title="Confirm Deletion"
                        content="Are you sure you want to delete this user?"
                      />
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Paper>
      )}
    </Grid2>
  );
};

export default observer(CustomerListing);
